/**
 * Per-project time inside a single store visit (#3452).
 *
 * A store visit is the container; the rep may work several projects inside it,
 * each as one or more task sessions (`visit_task_sessions`, one record per
 * work session — #1897). The in-store timer therefore has two levels: total
 * time at the store (the store leg) and a sub-total per project.
 *
 * Task session records carry no storeId, so the visit is scoped by TIME: the
 * sessions that began at or after the open store leg started. That works
 * without touching the session schema and stays correct across repeat visits
 * to the same store on the same day.
 */

import { spanMinutes } from './dayLegs';

export interface TaskSessionTiming {
  projectId: number;
  startTime: string; // ISO
  endTime?: string;
  durationMinutes?: number;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
}

export interface ProjectTime {
  projectId: number;
  /** Minutes already banked by closed sessions of this project in this visit. */
  minutes: number;
  /** Start of the session still running, if any — the caller ticks this live. */
  runningSince: Date | null;
}

export function projectTimesForVisit(
  sessions: TaskSessionTiming[],
  visitStart: Date | null
): Map<number, ProjectTime> {
  const times = new Map<number, ProjectTime>();
  if (!visitStart) return times;

  for (const session of sessions) {
    // Abandoned work is voided server-side; counting it would bill time the
    // CallLog no longer has. (#1507)
    if (session.status === 'abandoned') continue;
    const start = new Date(session.startTime);
    if (Number.isNaN(start.getTime()) || start < visitStart) continue;

    const entry = times.get(session.projectId) ?? {
      projectId: session.projectId,
      minutes: 0,
      runningSince: null,
    };

    if (session.endTime) {
      entry.minutes += session.durationMinutes ?? spanMinutes(start, new Date(session.endTime));
    } else if (session.status === 'active') {
      // Keep the earliest open session — a project should only have one.
      entry.runningSince =
        entry.runningSince && entry.runningSince < start ? entry.runningSince : start;
    }

    times.set(session.projectId, entry);
  }

  return times;
}

/** Compact label for a sub-total, e.g. `45m` or `1h 05m`. */
/** A wall-clock instant the way every screen spells one: "8:30 AM". */
export function formatClockTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function formatCompactMinutes(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes));
  if (safe < 60) return `${safe}m`;
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${hours}h ${mins.toString().padStart(2, '0')}m`;
}
